import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleDriveSyncGlobal } from "../../../data/hooks/use-google-drive-sync-global";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../ui/tooltip";
import { Check } from "lucide-react";
import { ReactComponent as GoogleDriveIcon } from "../../../static/icons/google-drive.svg";

export function GoogleDriveSyncIndicator() {
    const { isSyncing, progress, isComplete } = useGoogleDriveSyncGlobal();
    const navigate = useNavigate();
    const [showComplete, setShowComplete] = useState(false);

    useEffect(() => {
        if (isComplete) {
            setShowComplete(true);
            const timer = setTimeout(() => setShowComplete(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [isComplete]);

    if (!isSyncing && !showComplete) return null;

    const percentage = Math.round((progress ?? 0) * 100);
    const size = 28;
    const center = size / 2;
    const radius = 11;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progress ?? 0) * circumference;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    onClick={() => navigate("/settings/google-drive")}
                    className="relative flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-accent"
                    aria-label={`Google Drive sync: ${percentage}%`}
                >
                    {showComplete ? (
                        <Check className="h-6 w-6 text-green-500 animate-in zoom-in" />
                    ) : (
                        <div
                            className="relative flex items-center justify-center"
                            style={{ width: size, height: size }}
                        >
                            <svg
                                width={size}
                                height={size}
                                viewBox={`0 0 ${size} ${size}`}
                                className="absolute inset-0 animate-in fade-in"
                            >
                                <circle
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="text-muted-foreground/30"
                                />
                                <circle
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeDasharray={circumference}
                                    strokeDashoffset={offset}
                                    strokeLinecap="round"
                                    transform={`rotate(-90 ${center} ${center})`}
                                    className="text-primary transition-all duration-300"
                                />
                            </svg>
                            <GoogleDriveIcon className="h-3 w-3" />
                        </div>
                    )}
                </button>
            </TooltipTrigger>
            <TooltipContent>
                <span>{showComplete ? "Google Drive sync complete" : `Google Drive sync: ${percentage}%`}</span>
            </TooltipContent>
        </Tooltip>
    );
}
